import React from 'react';
import { Box, ButtonBase, CircularProgress, Tooltip, Typography } from '@mui/material';
import { tokens } from '../../../theme/tokens';
import {
  CameraWithPricing,
  formatDate,
  formatRelativeDate,
  getMarketPricingRefreshUi,
  isMarketPricingStale,
  useProductPriceFormatters,
} from '../../../utils/productDetailHelpers';
import { getDealerUrl } from '../../../utils/dealerUrl';
import { StoreLocationPricePanel } from '../StoreLocationPricePanel';
import { TradeInCreditLine } from '../TradeInCreditLine';
import { BUNDLE_TONE, DealerCard, Dot, WarnBanner } from './primitives';
import { buildEndedRebates, buildPrograms, buildSchedule, ProgramRow, shortDay, usd } from './dealerModel';
import { buildPricingWindows, exclusiveEnd, PricingWindow, toMs } from './pricingWindows';

const DAY = 86_400_000;

interface CalendarRow {
  key: string;
  label: string;
  kind: PricingWindow['kind'] | 'bundle';
  startMs: number | null;
  endMs: number | null;
  endExclusiveMs: number | null;
  text: string;
  /** A rebate that already ended (MOT-551): drawn muted, so the dealer sees when it expired. */
  ended?: boolean;
}

const BAR: Record<CalendarRow['kind'], { bg: string; ink: string }> = {
  map: { bg: tokens.line.default, ink: tokens.ink.body },
  rebate: { bg: tokens.accent.main, ink: tokens.ink.inverse },
  sale: { bg: tokens.signal.positive.solid, ink: tokens.ink.inverse },
  bundle: { bg: BUNDLE_TONE.ink, ink: tokens.ink.inverse },
};

/** An ended rebate: a quiet bar on the past side of Today, never the accent of a running one. */
const ENDED_BAR = { bg: tokens.line.default, ink: tokens.ink.muted };

const KIND: Record<ProgramRow['kind'], { bg: string; ink: string }> = {
  'Instant rebate': { bg: tokens.accent.tint, ink: tokens.accent.hover },
  Bundle: BUNDLE_TONE,
  Promotion: { bg: tokens.surface.tint, ink: tokens.ink.bodyAlt },
};

export interface DealerPricingTabProps {
  product: CameraWithPricing;
  isStoreScoped: boolean;
  onRefreshMarketPricing: () => void;
  refreshMarketPricingPending: boolean;
}

export function DealerPricingTab({ product, isStoreScoped, onRefreshMarketPricing, refreshMarketPricingPending }: DealerPricingTabProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', p: { xs: '16px', sm: '22px' }, bgcolor: tokens.surface.subtle }}>
      <PricingCalendar product={product} />
      <ProgramsCard product={product} />
      {isStoreScoped && (
        <DealerCard title="Your price" note="Resolved the way a sync resolves it">
          <Box sx={{ px: '18px', py: '14px' }}>
            <StoreLocationPricePanel product={product} />
            {product.id && <TradeInCreditLine productId={product.id} />}
          </Box>
        </DealerCard>
      )}
      <MarketCard product={product} onRefresh={onRefreshMarketPricing} refreshPending={refreshMarketPricingPending} />
    </Box>
  );
}

// ─── Calendar ────────────────────────────────────────────────────────────────

function PricingCalendar({ product }: { product: CameraWithPricing }) {
  const now = Date.now();
  const allWindows = buildPricingWindows(product, now);
  const priceWindows = allWindows.filter((w) => w.status !== 'ended');
  // Combo (bundle) offers ride the calendar too; they never change this item's own lowest ad price.
  const comboRows: CalendarRow[] = (product.pricingData?.comboOffers ?? [])
    .filter((o) => o.instantRebate > 0 && o.status !== 'expired')
    .map((o, i) => {
      const startMs = toMs(o.effectiveDate);
      const endMs = toMs(o.endDate);
      return { key: `combo-${i}`, label: 'Bundle offer', kind: 'bundle' as const, startMs, endMs, endExclusiveMs: exclusiveEnd(endMs), text: `Save ${usd(o.instantRebate)}${o.condition ? ` with ${o.condition}` : ''}` };
    });
  const liveRows: CalendarRow[] = [
    ...priceWindows.map((w) => ({ key: `${w.kind}-${w.startMs}`, label: w.label, kind: w.kind, startMs: w.startMs, endMs: w.endMs, endExclusiveMs: w.endExclusiveMs, text: w.kind === 'rebate' ? `−${usd(w.amount)}` : usd(w.amount) })),
    ...comboRows,
  ];
  const schedule = buildSchedule(product, now);

  // Axis: from the start of this month to the end of the month after the last
  // boundary, at least three months.
  const startOfMonth = (ms: number) => { const d = new Date(ms); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1); };
  const addMonths = (ms: number, n: number) => { const d = new Date(ms); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1); };
  const lastBoundary = Math.max(now, ...liveRows.flatMap((w) => [w.startMs, w.endMs]).filter((v): v is number => v != null && v > now));
  const axisStart = startOfMonth(now - 20 * DAY);
  const axisEnd = Math.max(addMonths(startOfMonth(lastBoundary), 1), addMonths(axisStart, 3));
  // Rebates that ended inside the axis ride below the running windows (MOT-551), so the
  // dealer sees when a rebate expired. They never move the axis or the schedule table.
  const endedRows: CalendarRow[] = allWindows
    .filter((w) => w.kind === 'rebate' && w.status === 'ended' && w.endMs != null && w.endMs >= axisStart)
    .map((w) => ({
      key: `ended-${w.amount}-${w.startMs}-${w.endMs}`,
      label: 'Instant rebate',
      kind: w.kind,
      startMs: w.startMs,
      endMs: w.endMs,
      endExclusiveMs: w.endExclusiveMs,
      text: `−${usd(w.amount)} · ended ${shortDay(w.endMs)}`,
      ended: true,
    }));
  const windows: CalendarRow[] = [...liveRows, ...endedRows];
  const span = axisEnd - axisStart;
  const pct = (ms: number) => Math.min(100, Math.max(0, ((ms - axisStart) / span) * 100));
  const months: Array<{ label: string; left: number }> = [];
  for (let m = axisStart; m < axisEnd; m = addMonths(m, 1)) {
    months.push({ label: new Date(m).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }), left: pct(m) });
  }
  const rangeLabel = `${new Date(axisStart).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })} – ${new Date(axisEnd - DAY).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })}`;
  const todayLeft = pct(now);

  const sub = (w: CalendarRow) =>
    w.startMs != null && w.endMs != null
      ? `${shortDay(w.startMs)} – ${shortDay(w.endMs)}`
      : w.startMs != null
        ? `since ${shortDay(w.startMs, true)}`
        : w.endMs != null
          ? `until ${shortDay(w.endMs, true)}`
          : 'No dates on file';

  return (
    <DealerCard title="Pricing calendar" note={`MAP, rebates and promotions for this SKU · ${rangeLabel}`}>
      {windows.length === 0 && schedule.length === 0 ? (
        <Typography sx={{ px: '18px', py: '16px', fontSize: '13px', color: tokens.ink.muted }}>
          No MAP, rebate or sale windows are on file for this product.
        </Typography>
      ) : (
        <>
          {windows.length > 0 && (
            <Box sx={{ px: '18px', pt: '16px', pb: '6px', display: 'flex', flexDirection: 'column', gap: '8px', overflowX: 'auto' }}>
              <Box sx={{ minWidth: 520, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box sx={{ position: 'relative', height: 18, ml: '150px' }}>
                  {months.map((m) => (
                    <Typography key={m.label + m.left} component="span" sx={{ position: 'absolute', top: 0, left: `${m.left}%`, fontSize: '11px', fontWeight: 600, color: tokens.ink.faint, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      {m.label}
                    </Typography>
                  ))}
                </Box>
                <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Box aria-hidden sx={{ position: 'absolute', top: -6, bottom: -6, left: 150, right: 0, pointerEvents: 'none', zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${todayLeft}%`, borderLeft: '2px dashed', borderColor: tokens.ink.primary, opacity: 0.35 }} />
                  </Box>
                  {windows.map((w) => {
                    const left = w.startMs != null ? pct(w.startMs) : 0;
                    const right = w.endExclusiveMs != null ? pct(w.endExclusiveMs) : 100;
                    const c = w.ended ? ENDED_BAR : BAR[w.kind];
                    return (
                      <Box key={w.key} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '142px minmax(0,1fr)' }, gap: '8px', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
                          <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: w.ended ? tokens.ink.muted : undefined }}>{w.label}{w.ended ? ' · ended' : ''}</Typography>
                          <Typography component="span" sx={{ fontSize: '11px', color: tokens.ink.faint }}>{sub(w)}</Typography>
                        </Box>
                        <Box sx={{ position: 'relative', height: 26, borderRadius: '6px', bgcolor: tokens.surface.sunken }}>
                          <Box sx={{ position: 'absolute', zIndex: 1, top: 0, bottom: 0, left: `${left}%`, width: `${Math.max(right - left, 1)}%`, borderRadius: '6px', bgcolor: c.bg, border: w.ended ? '1px dashed' : 'none', borderColor: w.ended ? tokens.ink.disabled : undefined, display: 'flex', alignItems: 'center', px: '8px', boxSizing: 'border-box', overflow: 'hidden' }}>
                            <Typography component="span" sx={{ fontSize: '11.5px', fontWeight: 600, color: c.ink, whiteSpace: 'nowrap' }}>
                              {w.text}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={{ ml: '150px', position: 'relative', height: 16 }}>
                  <Typography component="span" sx={{ position: 'absolute', left: `${todayLeft}%`, transform: 'translateX(-50%)', fontSize: '11px', fontWeight: 600 }}>Today</Typography>
                </Box>
              </Box>
            </Box>
          )}
          {schedule.length > 0 && (
            <Box sx={{ px: '18px', pt: '6px', pb: '16px', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '140px 110px minmax(0,1fr) 84px' }, gap: '12px', py: '8px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.faint, borderBottom: '1px solid', borderColor: tokens.line.hairline }}>
                <span>Dates</span><span style={{ textAlign: 'right' }}>Lowest ad price</span><span>Why</span><span style={{ textAlign: 'right' }}>Status</span>
              </Box>
              {schedule.map((r) => (
                <Box key={`${r.startMs}-${r.why}`} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr) auto', sm: '140px 110px minmax(0,1fr) 84px' }, gap: '4px 12px', alignItems: 'baseline', py: '9px', borderBottom: '1px solid', borderColor: tokens.surface.sunken, bgcolor: r.status === 'live' ? tokens.signal.positive.row : 'transparent' }}>
                  <Typography component="span" sx={{ fontSize: '12.5px', color: tokens.ink.body, fontVariantNumeric: 'tabular-nums' }}>{r.dates}</Typography>
                  <Typography component="span" sx={{ textAlign: 'right', fontSize: '13.5px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{usd(r.price)}</Typography>
                  <Typography component="span" sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>{r.why}</Typography>
                  <Typography component="span" sx={{ textAlign: 'right', fontSize: '12px', fontWeight: 600, color: r.status === 'live' ? tokens.signal.positive.ink : tokens.ink.muted }}>
                    {r.status === 'live' ? 'Live now' : 'Upcoming'}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </DealerCard>
  );
}

// ─── Programs ────────────────────────────────────────────────────────────────

function ProgramsCard({ product }: { product: CameraWithPricing }) {
  const programs = buildPrograms(product);
  const ended = buildEndedRebates(product);
  return (
    <DealerCard title="Rebates, bundles & promotions" count={programs.length || undefined} note="Applied automatically when you push">
      {programs.length === 0 ? (
        <Typography sx={{ px: '18px', py: '16px', fontSize: '13px', color: tokens.ink.muted }}>
          No rebates, bundles or promotions on this product right now. New programs from {product.brand} show up here as soon as they load.
        </Typography>
      ) : (
        programs.map((g) => {
          const k = KIND[g.kind];
          const live = g.status === 'live';
          return (
            <Box key={g.id} sx={{ display: 'flex', flexDirection: 'column', gap: '10px', px: '18px', py: '14px', borderBottom: '1px solid', borderColor: tokens.line.hairline, '&:last-of-type': { borderBottom: 'none' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 20, px: '8px', borderRadius: '6px', bgcolor: k.bg, color: k.ink, fontSize: '11.5px', fontWeight: 600 }}>{g.kind}</Box>
                <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 600 }}>{g.name}</Typography>
                <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 600, color: tokens.signal.positive.ink, fontVariantNumeric: 'tabular-nums' }}>{g.amount}</Typography>
                <Box sx={{ flex: '1 1 auto' }} />
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: live ? tokens.signal.positive.ink : tokens.ink.muted }}>
                  <Dot color={live ? tokens.signal.positive.solid : tokens.ink.disabled} />
                  {g.statusLabel}
                </Box>
              </Box>
              {g.meta && <Typography sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>{g.meta}</Typography>}
              {g.bundleCondition && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '12px', py: '9px', border: '1px dashed', borderColor: tokens.line.default, borderRadius: '9px', bgcolor: tokens.surface.sunken, fontSize: '12.5px', flexWrap: 'wrap' }}>
                  <Typography component="span" sx={{ fontSize: 'inherit', color: tokens.ink.body }}>This item + {g.bundleCondition}</Typography>
                  <Box sx={{ flex: '1 1 auto' }} />
                  <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 600, color: tokens.signal.positive.ink }}>{g.amount} on the pair</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.faint, mr: '4px' }}>Eligible</Typography>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: 24, px: '8px', borderRadius: '7px', border: '1px solid', borderColor: tokens.accent.border, bgcolor: tokens.signal.info.row, fontSize: '12px', color: tokens.ink.body }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>{product.name || product.model}</Box>
                  {product.sku && <Box component="span" sx={{ color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{product.sku}</Box>}
                </Box>
              </Box>
            </Box>
          );
        })
      )}
      {ended.length > 0 && (
        <Box sx={{ px: '18px', py: '12px', borderTop: '1px solid', borderColor: tokens.line.hairline, bgcolor: tokens.surface.subtle, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Typography variant="overline" sx={{ color: tokens.ink.faint }}>Recently ended</Typography>
          {ended.map((r) => (
            <Box key={r.id} sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', fontSize: '12.5px' }}>
              <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 600, color: tokens.ink.muted, fontVariantNumeric: 'tabular-nums' }}>{r.amount} instant rebate</Typography>
              <Typography component="span" sx={{ fontSize: 'inherit', color: tokens.ink.muted, fontVariantNumeric: 'tabular-nums' }}>{r.dates}</Typography>
              {r.lowestAd && <Typography component="span" sx={{ fontSize: 'inherit', color: tokens.ink.faint }}>was {r.lowestAd}</Typography>}
              <Box sx={{ flex: '1 1 auto' }} />
              <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, color: tokens.ink.faint }}>{r.endedLabel}</Typography>
            </Box>
          ))}
          <Typography sx={{ fontSize: '11.5px', color: tokens.ink.faint }}>Your price went back to MAP when each one ended. The Rebate Archive on Price History lists every ended rebate.</Typography>
        </Box>
      )}
    </DealerCard>
  );
}

// ─── Market pricing ──────────────────────────────────────────────────────────

function MarketCard({ product, onRefresh, refreshPending }: { product: CameraWithPricing; onRefresh: () => void; refreshPending: boolean }) {
  const { saleDisplay } = useProductPriceFormatters();
  const m = product.marketPricing;
  const dealers = m?.dealerPrices ?? [];
  const has = !!m && (m.streetPriceLow != null || dealers.length > 0);
  const ui = getMarketPricingRefreshUi(m);
  const stale = isMarketPricingStale(m);
  const checked = m?.checkedAt ? formatRelativeDate(m.checkedAt) || formatDate(m.checkedAt) : null;
  const refreshing = ui.state === 'refreshing' || refreshPending;
  const cooldown = ui.state === 'cooldown';
  const nextDays = cooldown ? ui.nextRefreshInDays : 0;
  const yourPrice = saleDisplay(product).sellingPrice || product.price || 0;
  const allUnder = has && dealers.length > 0 && yourPrice > 0 && dealers.every((d) => d.price != null && d.price < yourPrice);
  const avgGap = allUnder && m?.streetPriceAvg ? Math.round(yourPrice - m.streetPriceAvg) : null;

  return (
    <Box sx={{ bgcolor: tokens.surface.card, border: '1px solid', borderColor: tokens.line.default, borderRadius: '12px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', px: '18px', py: '14px', flexWrap: 'wrap', borderBottom: '1px solid', borderColor: tokens.line.hairline }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <Typography component="span" sx={{ fontSize: '14px', fontWeight: 600 }}>Market pricing</Typography>
          {checked && <Typography component="span" sx={{ fontSize: '12px', color: stale ? tokens.signal.caution.ink : tokens.ink.faint }}>Checked {checked}</Typography>}
          {stale && has && (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: '6px', py: '1px', borderRadius: '4px', border: '1px solid', borderColor: tokens.signal.caution.border, bgcolor: tokens.signal.caution.tint, color: tokens.signal.caution.ink, fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.06em' }}>STALE</Box>
          )}
        </Box>
        <Tooltip
          arrow
          title={refreshing ? 'A market pricing refresh is already running for this product.' : cooldown ? `Market pricing can be refreshed once every 7 days. Next refresh in ${nextDays} day${nextDays === 1 ? '' : 's'}.` : 'Look up current dealer and street prices. This can take up to a minute.'}
        >
          <span>
            <ButtonBase
              onClick={onRefresh}
              disabled={refreshing || cooldown}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: 30, px: '11px', border: '1px solid', borderColor: tokens.line.default, borderRadius: '8px', bgcolor: tokens.surface.card, color: tokens.ink.bodyAlt, fontSize: '12.5px', fontWeight: 600, opacity: refreshing || cooldown ? 0.6 : 1, '&:hover': { borderColor: tokens.ink.disabled, color: tokens.ink.primary } }}
            >
              {refreshing ? <CircularProgress size={11} /> : (
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
                  <path d="M11.5 7a4.5 4.5 0 01-7.7 3.2M2.5 7a4.5 4.5 0 017.7-3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  <path d="M2.5 11V8.2h2.8M11.5 3v2.8H8.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              <span>{refreshing ? 'Refreshing…' : cooldown ? `Next refresh in ${nextDays}d` : 'Refresh'}</span>
            </ButtonBase>
          </span>
        </Tooltip>
      </Box>
      {has ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', px: '18px', pt: '14px', pb: '16px' }}>
          {m?.streetPriceLow != null && m?.streetPriceHigh != null && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '13px', flexWrap: 'wrap' }}>
              <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{usd(m.streetPriceLow)} – {usd(m.streetPriceHigh)}</Typography>
              <Typography component="span" sx={{ fontSize: 'inherit', color: tokens.ink.muted }}>
                street across {dealers.length} dealer{dealers.length === 1 ? '' : 's'}{m.streetPriceAvg != null ? ` · avg ${usd(m.streetPriceAvg)}` : ''}
              </Typography>
            </Box>
          )}
          {stale && <WarnBanner>These street prices are stale{checked ? ` (${checked})` : ''}. Refresh before using them for MAP or list-price decisions.</WarnBanner>}
          {allUnder && !stale && (
            <WarnBanner>
              Every tracked dealer is below your {usd(yourPrice)}. Street sits around {usd(m?.streetPriceAvg ?? 0)}{avgGap != null && avgGap > 0 ? `, about $${avgGap} under` : ''}.
            </WarnBanner>
          )}
          {dealers.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: tokens.line.hairline, borderRadius: '10px', overflow: 'hidden' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr) auto auto', sm: 'minmax(0,1fr) 110px 100px' }, gap: '12px', px: '12px', py: '7px', bgcolor: tokens.surface.subtle, borderBottom: '1px solid', borderColor: tokens.line.hairline, fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.faint }}>
                <span>Dealer</span><span style={{ textAlign: 'right' }}>Price</span><span style={{ textAlign: 'right' }}>Stock</span>
              </Box>
              {dealers.map((d) => {
                const url = getDealerUrl(d.dealer, d.url, product.brand, product.model || product.sku || '');
                const under = yourPrice > 0 && d.price != null && d.price < yourPrice;
                return (
                  <Box key={`${d.dealer}-${d.url || ''}-${d.price}`} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr) auto auto', sm: 'minmax(0,1fr) 110px 100px' }, gap: '12px', alignItems: 'center', px: '12px', py: '8px', borderBottom: '1px solid', borderColor: tokens.surface.sunken, fontSize: '13px' }}>
                    <Typography component="span" sx={{ fontSize: 'inherit', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {url ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{d.dealer}</a> : d.dealer}
                    </Typography>
                    <Typography component="span" sx={{ textAlign: 'right', fontSize: 'inherit', fontWeight: 600, color: under ? tokens.signal.positive.ink : tokens.ink.primary, fontVariantNumeric: 'tabular-nums' }}>{usd(d.price)}</Typography>
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px', fontSize: '12.5px', color: d.inStock ? tokens.signal.positive.ink : tokens.ink.muted }}>
                      <Dot color={d.inStock ? tokens.signal.positive.solid : tokens.ink.disabled} />
                      {d.inStock ? 'In stock' : d.inStock === false ? 'Out of stock' : '—'}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
          {(m?.msrp != null || m?.msrpSource) && (
            <Typography sx={{ fontSize: '11.5px', color: tokens.ink.faint }}>
              MSRP {m?.msrp != null ? usd(m.msrp) : '—'}{m?.msrpSource ? ` via ${m.msrpSource}` : ''}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography sx={{ px: '18px', pt: '14px', pb: '16px', fontSize: '13px', color: tokens.ink.muted }}>
          No dealer prices yet. Refresh to look up current street pricing.
        </Typography>
      )}
    </Box>
  );
}
